import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import BillingReports from './pages/BillingReports';
import SpecializationManagement from './pages/SpecializationManagement';
import ClassTypeManagement from './pages/ClassTypeManagement';
import Login from './pages/Login';
import adminService from './services/adminService';
import { isTokenExpired } from './utils/helpers';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only check auth once on mount
    let isMounted = true;
    
    const checkAuthentication = async () => {
      const token = localStorage.getItem('admin_token');
      
      if (!token || isTokenExpired(token)) {
        if (isMounted) {
          setCurrentUser(null);
          setAuthChecked(true);
        }
        return;
      }

      try {
        const user = await adminService.getCurrentUser();
        if (isMounted) {
          if (user.is_staff || user.is_superuser) {
            setCurrentUser(user);
          } else {
            // User is not admin, clear token
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_refresh');
            setCurrentUser(null);
          }
          setAuthChecked(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (isMounted) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh');
          setCurrentUser(null);
          setAuthChecked(true);
        }
      }
    };

    checkAuthentication();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once

  const handleSetCurrentUser = useCallback((user) => {
    setCurrentUser(user);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await adminService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Loading screen while checking authentication
  if (!authChecked) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      <Routes>
        <Route
          path="/login"
          element={
            currentUser ? (
              <Navigate to="/analytics" replace />
            ) : (
              <Login setCurrentUser={handleSetCurrentUser} />
            )
          }
        />
        
        <Route
          path="/*"
          element={
            !currentUser ? (
              <Navigate to="/login" replace />
            ) : (
              <Box sx={{ width: '100%' }}>
                <Header user={currentUser} onLogout={handleLogout} />
                <Box sx={{ display: 'flex' }}>
                  <Sidebar />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      p: 0,
                      width: { sm: 'calc(100% - 240px)' },
                      mt: { sm: '64px' },
                    }}
                  >
                    <Routes>
                      <Route path="/" element={<Navigate to="/analytics" replace />} />
                      <Route path="/users" element={<UserManagement />} />
                      <Route path="/billing" element={<BillingReports />} />
                      <Route path="/analytics" element={<Dashboard />} />
                      <Route path="/specializations" element={<SpecializationManagement />} />
                      <Route path="/class-types" element={<ClassTypeManagement />} />
                    </Routes>
                  </Box>
                </Box>
              </Box>
            )
          }
        />
      </Routes>
    </Box>
  );
}

export default App;
